// ** React Imports **
import { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from 'notistack';

// ** Material-UI **
import { TextField, Autocomplete, Box } from "@mui/material";

// ** Third Party **
import { useMutation } from "react-query";
import debounce from "lodash/debounce";

// ** API **
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

// ** Components **
import StepProgressIndicator from '../../../components/authentication/StepProgressIndicator';
import InputField from '../../../components/authentication/InputField';
import SelectLocation from 'src/@core/components/SelectLocation';

// ** Styles **
import './RegisterStepLocation.css';

/**
 * RegisterStepLocation Component (Step 2)
 * 
 * Business location selection step
 * 
 * Features:
 * - Map with location picker
 * - Address search
 * - Selected address display
 * - Business location name
 * - Business location username
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.userData - User data from Step 0
 * @param {Object} props.businessData - Business data from Step 1
 * @param {Object} props.initialData - Pre-filled data if returning to this step
 * @param {Function} props.onComplete - Callback when step completed
 */
const RegisterStepLocation = ({ userData, businessData, initialData, onComplete, onStepClick }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openLocation, setOpenLocation] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(initialData?.selectedAddress || null);
  const [currency, setCurrency] = useState(initialData?.currency || null);
  const [currencySymbol, setCurrencySymbol] = useState(initialData?.currencySymbol || null);
  const [options, setOptions] = useState([]);
  const [addressError, setAddressError] = useState("");
  const [usernameState, setUsernameState] = useState({
    isValid: true,
    error: "",
  });

  // ** React Hook Form **
  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      businessLocationName: initialData?.businessLocationName || businessData?.name || "",
      businessLocationUserName: initialData?.businessLocationUserName || "",
      physicalAddress: initialData?.physicalAddress || "",
    },
  });

  const formValues = watch();

  // ** Username Suggestions Mutation **
  const { mutate: getUsernameSuggestions } = useMutation({
    mutationFn: async (input) => {
      const res = await axiosInstance.post(
        `${ApiEndPoints.BUSINESS.userName}`,
        { input }
      );
      return res.data;
    },
  });

  // ** Handle Username Search with Debounce **
  const handleSearch = useMemo(
    () =>
      debounce((value) => {
        if (value?.trim()) {
          getUsernameSuggestions(value, {
            onSuccess: (data) => {
              setOptions(data?.data?.data || []);
              setUsernameState({
                isValid: data.message !== "user name already taken",
                error:
                  data.message === "user name already taken"
                    ? data.message
                    : "",
              });
            },
          });
        }
      }, 500),
    [getUsernameSuggestions]
  );

  // Auto-fill business location name from business name
  useEffect(() => {
    if (businessData?.name && !formValues.businessLocationName) {
      setValue("businessLocationName", businessData.name);
    }
  }, [businessData, formValues.businessLocationName, setValue]);

  /**
   * Fetch currency by country name
   */
  const fetchCurrencyByCountryName = async (countryName) => {
    if (!countryName) {
      setCurrency(null);
      setCurrencySymbol(null);
      return;
    }

    try {
      const encodedCountryName = encodeURIComponent(countryName);
      const url = `${ApiEndPoints.COMMON.currency}/country/${encodedCountryName}`;
      const response = await axiosInstance.get(url);

      if (response.data?.status === "success" && response.data?.data?.currency) {
        const currencyData = response.data.data.currency;
        setCurrency(currencyData.currency);
        setCurrencySymbol(currencyData.symbol);
      } else {
        setCurrency(null);
        setCurrencySymbol(null);
      }
    } catch (error) {
      console.error("Currency fetch error:", error);
      setCurrency(null);
      setCurrencySymbol(null);
    }
  };

  /**
   * Handle map address selection
   */
  const handleMapAddressSelect = async (address) => {
    setSelectedAddress({
      lng: address.longitude,
      lat: address.latitude,
      physicalAddress: address.physicalAddress || address,
      iso2: address.iso2,
      iso3: address.iso3,
      countryCode: address.iso2 || address.countryCode,
      countryName: address.countryName,
      postalCode: address.postalCode,
      state: address.state,
      stateCode: address.stateCode,
      city: address.city,
    });

    // Clear address error when address is selected
    setAddressError("");

    // Update form field
    setValue("physicalAddress", address.physicalAddress);

    // Fetch currency for the country
    if (address.countryName) {
      await fetchCurrencyByCountryName(address.countryName);
    }
  };

  /**
   * Handle form submission
   */
  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Validation - Check address first
      if (!selectedAddress) {
        setAddressError("please select a location on the map");
        enqueueSnackbar("please select a location on the map", {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });
        setIsSubmitting(false);
        return;
      }

      // Check other required fields
      if (!data.businessLocationName || !data.businessLocationUserName) {
        enqueueSnackbar("please fill all required fields", {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });
        setIsSubmitting(false);
        return;
      }

      // Check username validity
      if (!usernameState.isValid) {
        enqueueSnackbar("username is already taken, please choose another", {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });
        setIsSubmitting(false);
        return;
      }

      // Move to next step with location data
      onComplete({
        businessLocationName: data.businessLocationName,
        businessLocationUserName: data.businessLocationUserName,
        physicalAddress: data.physicalAddress,
        coordinates: selectedAddress,
        currency: currency,
        currencySymbol: currencySymbol,
      });
    } catch (error) {
      console.error("Location error:", error);
      enqueueSnackbar("please check all fields and try again", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-step-location">
      <div className="register-step-location__card">
        {/* Progress Indicator */}
        <StepProgressIndicator currentStep={2} totalSteps={3} onStepClick={onStepClick} />

        {/* Title */}
        <h2 className="register-step-location__title">business location</h2>

        {/* Form */}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="register-step-location__form">
          
          {/* Map Section */}
          <div className="register-step-location__map-section">
            <label className="register-step-location__label">location*</label>
            
            {/* Map Display / Button */}
            <div 
              className="register-step-location__map-placeholder"
              onClick={() => setOpenLocation(true)}
              style={{ 
                borderColor: addressError ? '#d32f2f' : '#ECECEC'
              }}
            >
              {selectedAddress ? (
                <div className="register-step-location__map-preview">
                  <div className="register-step-location__map-icon">📍</div>
                  <div className="register-step-location__map-text">
                    <div className="register-step-location__map-address">
                      {selectedAddress.physicalAddress}
                    </div>
                    <div className="register-step-location__map-action">
                      click to change location
                    </div>
                  </div>
                </div>
              ) : (
                <div className="register-step-location__map-empty">
                  <div className="register-step-location__map-icon">🗺️</div>
                  <div className="register-step-location__map-action">
                    click to select location on map
                  </div>
                </div>
              )}
            </div>
            {addressError && (
              <span className="register-step-location__error" style={{
                color: '#d32f2f',
                fontSize: '14px',
                marginTop: '4px',
                display: 'block'
              }}>
                {addressError}
              </span>
            )}
          </div>

          {/* Business Location Name */}
          <InputField
            {...register("businessLocationName", { required: "business location name is required" })}
            type="text"
            placeholder="business location name*"
            value={formValues.businessLocationName}
            error={errors.businessLocationName ? errors.businessLocationName.message : ''}
            required
          />

          {/* Business Location Username with @ prefix and suggestions */}
          <div className="register-step-location__field">
            <Controller
              name="businessLocationUserName"
              control={control}
              rules={{
                required: "business location username is required",
                validate: (value) =>
                  usernameState.isValid || "username is already taken",
              }}
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  options={options}
                  onInputChange={(event, value) => {
                    handleSearch(value);
                    field.onChange(value);
                  }}
                  value={field.value || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...field}
                      value={field.value || ""}
                      fullWidth
                      placeholder="business location username*"
                      error={!!errors.businessLocationUserName}
                      helperText={errors.businessLocationUserName?.message}
                      sx={{
                        "& .MuiInputBase-root": {
                          borderRadius: "10px",
                          background: "#F4F5FA",
                          fontSize: "16px",
                          padding: "12px 16px",
                          border: "1px solid #ECECEC",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiInputBase-input": {
                          padding: "0 !important",
                          paddingLeft: "24px !important",
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Box
                            component="span"
                            sx={{ color: "#888", fontWeight: 500, mr: 1 }}
                          >
                            @
                          </Box>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </div>

          {/* Continue Button */}
          <button
            type="submit"
            className="register-step-location__submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'processing...' : 'continue'}
          </button>
        </form>
      </div>

      {/* Location Selection Dialog */}
      <SelectLocation
        open={openLocation}
        setOpen={setOpenLocation}
        onSelect={handleMapAddressSelect}
        initialLocation={selectedAddress}
      />
    </div>
  );
};

export default RegisterStepLocation;


